/**
 * A CHAVE do cartão de onboarding — as cinco etapas da apresentação do AAWZ —
 * e o detector que diz se uma parte do turno a carrega.
 *
 * ★ **Por que a chave mora numa constante, e num arquivo só dela.**
 *
 * Mesmo motivo do irmão `chave-da-boas-vindas.js`, escrito nesta mesma branch:
 * o backend passou a tratar o onboarding como turnos próprios
 * (`core/onboarding/`), resolvidos INTEIROS pelo servidor — sem chamar modelo
 * nenhum —, e o que diz "este turno É uma etapa do onboarding" é uma parte de
 * tool marcada com esta chave. Com o literal espalhado pelo registry e por
 * quem precisar detectá-lo, trocá-lo seria caçar strings iguais em arquivos
 * diferentes; com ele aqui, é UMA linha.
 *
 * ★ **Zero imports, de propósito.** Este arquivo é folha pelo mesmo motivo de
 * `chave-de-tool-de-front.js` e `chave-da-boas-vindas.js`: quem precisa dele
 * são dois lados que não podem se importar — o registry parcial da plataforma
 * (`ui-registry/plataforma.js`, que puxa o catálogo de navegação inteiro) e a
 * fiação da tela (`views/chat/view-chat.vue`). Um import do registry a partir
 * da view fecharia o ciclo que derrubou 8 suítes em 26/08 ([T010]).
 *
 * ⚠️ **O que NÃO está aqui: um `turnoTemCartaoDeOnboarding`.** O irmão da
 * boas-vindas tem um porque lá existe um CONSUMIDOR real — o gate que faz o
 * bloco antigo ceder a vez ao cartão (`view-chat-boas-vindas.mixin.js`). O
 * onboarding não tem caminho antigo a desligar, então a função nasceria sem
 * quem a chamasse. Função sem consumidor é peso morto que a próxima pessoa lê
 * como contrato.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

/**
 * A chave que o backend manda em `ui.component` de cada etapa do onboarding
 * (`COMPONENTE_ONBOARDING`, `core/onboarding/cartoes.py`).
 *
 * ⚠️ Espelho MANUAL do backend, como todo `ui.component` deste módulo. Trocar
 * aqui e no backend é o mesmo gesto; trocar só de um lado faz o cartão cair no
 * `fallback-card` com o motivo `chave_desconhecida` — visível na tela, que é
 * exatamente o desfecho que o registry existe para dar.
 *
 * ★ Namespace `plataforma/` porque é cartão da PLATAFORMA, não de um domínio
 * de negócio — a mesma razão declarada no backend e em `plataforma/navegar`,
 * `plataforma/abrir-cliente` e `plataforma/boas-vindas`.
 */
export const CHAVE_DO_CARTAO_DE_ONBOARDING = 'plataforma/onboarding'

/**
 * A ação que o cartão manda pelo canal genérico `respond` quando o consultor
 * pede para rodar a skill da etapa 4.
 *
 * ★ **Por que ela mora AQUI, e não no `.vue` do cartão.** O nome é um contrato
 * entre DOIS arquivos que não podem se importar: o cartão (que emite) e
 * `views/chat/view-chat.vue` (que roteia para `executarAtalho`, o MESMO
 * handler do menu do `/`). A view importar um `.vue` de cartão para ler uma
 * constante seria pior que o literal duplicado; este arquivo já é a folha sem
 * imports que os dois lados alcançam, e o nome da ação é parte do mesmo
 * contrato que a chave.
 *
 * ⚠️ É só o NOME do canal — quem executa o atalho continua sendo
 * `apiChatAtalhos.sendAtalho`, por `view-chat.vue::executarAtalho`. Não há um
 * segundo caminho de execução de atalho, e não pode haver: o transporte de
 * atalho fala com `/chat` direto, nunca com `POST /runs`
 * (`api/apiChatAtalhos.js`, "Por que não `apiChat.sendMessage()`").
 */
export const ACAO_EXECUTAR_ATALHO = 'executar-atalho'

/**
 * Esta parte (viva) ou esta chamada (persistida) é uma etapa do onboarding?
 *
 * O discriminante é `ui.component`, e não o nome da marca: o backend declara
 * que `onboarding_primeiro_contato` **não é** uma tool registrada (nenhum
 * modelo a chama, o `ToolRegistry` nunca a oferece) e que o nome existe só
 * para quem lê o JSONB. Casar por ele seria combinar com um campo que pode
 * mudar sem o front saber.
 *
 * @param {Object} parte Uma parte de `toolParts` ou um item de `tool_calls[]`.
 * @returns {boolean}
 */
export function ehCartaoDeOnboarding(parte) {
  if (!parte || typeof parte !== 'object') return false
  const ui = parte.ui
  if (!ui || typeof ui !== 'object') return false
  return ui.component === CHAVE_DO_CARTAO_DE_ONBOARDING
}

/**
 * O `name` do par que o servidor espelha em `_mensagens.tool_calls` para cada
 * etapa (`core/onboarding/cartoes.NOME_DA_MARCA`).
 *
 * ⚠️ Espelho MANUAL do backend, como todo nome que atravessa o fio. Existe
 * porque a etapa 1 é marcada SEM cartão (o corpo dela é o formulário de
 * perguntas), então `ui.component` não a alcança — e ela é justamente a etapa
 * em que a conversa fala sobre a PESSOA, que é o que precisa ser reconhecido.
 */
export const NOME_DA_MARCA_DO_ONBOARDING = 'onboarding_primeiro_contato'

/**
 * A ação do canal `respond` que AVANÇA a apresentação para a etapa seguinte.
 *
 * ★ Ela vira uma MENSAGEM comum: o servidor entrega a próxima etapa a cada
 * mensagem da pessoa (`usecase_ia_crm_chat._etapa_do_onboarding`), então o
 * botão não precisa de rota nova — precisa de algo a dizer. O texto vem no
 * payload porque quem escreve a copy é o cartão, não a view.
 */
export const ACAO_AVANCAR_ETAPA = 'avancar-etapa'

/**
 * Esta chamada persistida é uma etapa do onboarding — com cartão ou sem?
 *
 * DUAS fontes, pelo mesmo motivo de `core/onboarding/progresso.py`: o cartão
 * (etapas 2-4) e o nome do marcador (etapa 1, que não desenha cartão).
 *
 * @param {Object} chamada Item de `message.tool_calls[]`.
 * @returns {boolean}
 */
export function ehMarcadorDeOnboarding(chamada) {
  if (!chamada || typeof chamada !== 'object') return false
  if (ehCartaoDeOnboarding(chamada)) return true
  return chamada.name === NOME_DA_MARCA_DO_ONBOARDING
}

/**
 * A conversa está DENTRO da apresentação agora?
 *
 * A régua é a ÚLTIMA mensagem do assistente: se ela é um turno do onboarding
 * (uma etapa, ou a própria boas-vindas), a apresentação está conduzindo — e
 * quem fala sobre a pessoa é ela, não os mecanismos que leem a conversa por
 * fora. Olhar a conversa inteira manteria a supressão ligada para sempre,
 * depois que o roteiro terminasse.
 *
 * @param {Array} mensagens A conversa (`messages` do store).
 * @param {Function} ehBoasVindas Detector do cartão de boas-vindas.
 * @returns {boolean}
 */
export function onboardingConduzindo(mensagens, ehBoasVindas) {
  const lista = Array.isArray(mensagens) ? mensagens : []
  for (let i = lista.length - 1; i >= 0; i--) {
    const mensagem = lista[i]
    if (!mensagem || mensagem.role !== 'assistant') continue
    const chamadas = Array.isArray(mensagem.tool_calls) ? mensagem.tool_calls : []
    if (chamadas.some(ehMarcadorDeOnboarding)) return true
    if (typeof ehBoasVindas === 'function' && chamadas.some(ehBoasVindas)) return true
    return false
  }
  return false
}

/**
 * A ação do canal `respond` que ENTREGA as respostas do formulário da etapa 1.
 *
 * ★ **Por que ela existe, se `ACAO_AVANCAR_ETAPA` já manda texto.** Aquela
 * carrega uma FALA ("Continuar", "Pular esta etapa") — uma frase de gente que
 * vira a bolha da pessoa na conversa. Esta carrega DADO: os pares
 * pergunta/resposta que o consultor digitou, que a view precisa formatar antes
 * de mandar (o backend só reconhece as respostas do gestor num formato
 * declarado — ver `utils/perguntas-ao-gestor.js::PREFIXO_RESPOSTAS`, espelho
 * de `core/entrega/material.py`). Empurrar os dois pelo mesmo nome de ação
 * obrigaria a view a adivinhar, pelo formato do payload, qual dos dois chegou.
 *
 * ⚠️ Quem LIGA esta ação à rota da view é `views/chat/view-chat.vue` — o
 * cartão só emite. Mesma régua do `ACAO_EXECUTAR_ATALHO` logo acima: cartão
 * não conhece transporte.
 *
 * Payload: `{respostas: [{pergunta, resposta}]}`, com a PERGUNTA escrita por
 * extenso (não o `id`) — é ela que a pessoa leu na tela, e é ela que o
 * servidor precisa ver ao lado da resposta.
 */
export const ACAO_RESPONDER_PERGUNTAS = 'responder-perguntas'

/**
 * A ação do canal `respond` que abre a conexão de um conector DENTRO do chat
 * (19/09/2026).
 *
 * ★ **Por que passa pelo `respond` e não é um `GlobalBus.$emit` do próprio
 * cartão.** Mesma régua do `ACAO_EXECUTAR_ATALHO`: cartão não conhece
 * transporte. Ele diz o que a pessoa pediu ("conectar isto"); quem traduz em
 * canal é `views/chat/view-chat.vue`, que já é o único roteador de tudo o que
 * sobe de um cartão. Um `$emit` direto criaria a segunda porta — e a segunda
 * porta nasce sem o que a view faz em volta.
 *
 * ⚠️ **Não substitui a navegação, convive com ela.** Só os conectores que têm
 * modal nesta superfície emitem esta ação (`constants/Conectores.js`
 * ::`modalDoConector`); os demais continuam abrindo a tela do módulo pela
 * `rota` do catálogo do backend. Conector novo publicado lá entra funcionando
 * sem passar por aqui.
 *
 * Payload: `{id}` — o `id` do conector (`email_agenda`, `whatsapp`), que é a
 * chave do catálogo, nunca o rótulo (copy).
 */
export const ACAO_CONECTAR_FERRAMENTA = 'conectar-ferramenta'

/**
 * A primeira linha da mensagem que leva as respostas da ETAPA 1.
 *
 * ⚠️ **Ela existe para NÃO ser o prefixo do parecer.** A view formata os pares
 * com `montarRespostasPareadas`, e o default daquele util é
 * `'Respondendo às perguntas em aberto do parecer:'` — a etiqueta que
 * `core/entrega/material.py::_respostas_do_gestor` procura com `startswith`,
 * sem conferir de que turno a mensagem veio. Herdando o default, o onboarding
 * produzia dois defeitos ao mesmo tempo:
 *
 * - **visível**: a bolha do consultor dizia "perguntas em aberto do parecer"
 *   no PRIMEIRO CONTATO, onde não existe parecer nem painel;
 * - **mudo**: as respostas sobre o escritório entravam no rascunho de entrega
 *   daquela conversa como se fossem respostas ao parecer.
 *
 * O FORMATO dos blocos (`N. pergunta\nResposta: …`) continua sendo o do util —
 * é contrato, e reusá-lo é o certo. O que não se herda é a copy.
 *
 * ★ O onboarding não parseia esta mensagem: qualquer texto do consultor faz o
 * servidor entregar a etapa seguinte (`core/onboarding/turno.py`). O prefixo
 * aqui é para a PESSOA ler, e para não colidir com o do parecer.
 */
export const PREFIXO_RESPOSTAS_DO_ONBOARDING = 'Sobre o meu escritório:'

/**
 * As falas que os BOTÕES do cartão mandam — "Continuar" (das etapas E da
 * boas-vindas, que é o mesmo gesto) e "Pular esta etapa".
 *
 * ★ [21/09/2026] Viraram CONTRATO, não copy. O servidor passou a avançar a
 * apresentação SÓ com a resposta do cartão (`core/onboarding/falas.py`):
 * fala livre no meio dela — o "oi" de quem clicou em "Continuar sem a
 * apresentação" — vai ao modelo, e a etapa fica pendente para os botões.
 * Trocar o texto de um lado só não levanta erro nenhum: o clique passa a ser
 * lido como fala livre e a apresentação simplesmente para de avançar. A
 * junção (`utils/falas-do-onboarding.unit.js`, `describeComBackend`) lê o
 * arquivo do backend. A copy do cartão (`onboarding/copy.js`) importa daqui.
 */
export const FALA_DO_CONTINUAR = 'Continuar'
export const FALA_DO_PULAR = 'Pular esta etapa'

/**
 * O `id` da mensagem que carrega a etapa CORRENTE da apresentação — a ÚLTIMA
 * parte de onboarding do thread — ou `null` quando não há nenhuma.
 *
 * ★ **Por que o veredito sai do THREAD, e não do cartão nem de um clique.**
 * O `value` da tool descreve a etapa (posição, título, perguntas) e nada nele
 * sabe se vieram turnos depois; `posicao`/`total` descrevem o ROTEIRO, não a
 * conversa. E estado local (o `recolhido` do cartão, um `avancou` na bolha)
 * morre no F5 — foi assim que, ao recarregar, as etapas nasciam todas
 * abertas, empilhando cartões altos de histórico.
 *
 * A conversa, essa, sobrevive: ela vem do servidor a cada abertura. Derivar
 * daqui faz o veredito atravessar reload, remontagem e troca de conversa sem
 * nada ser guardado em lugar nenhum.
 *
 * ⚠️ Percorre de trás para frente e para na primeira: é a última que importa,
 * e uma conversa longa não paga a varredura inteira.
 *
 * @param {Array} mensagens `messages` do store.
 * @returns {?string}
 * @example idDaMensagemDaEtapaCorrente([]) // → null
 */
export function idDaMensagemDaEtapaCorrente(mensagens) {
  const lista = mensagens || []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    const mensagem = lista[i]
    const chamadas = (mensagem && mensagem.tool_calls) || []
    if (chamadas.some(ehCartaoDeOnboarding)) return mensagem.id || null
  }
  return null
}
